import { emit } from "@/src/hooks/useListener";
import { ALERT_EVENTS } from "@/src/lib/constants";
import axios, { AxiosError } from "axios";
import { setBookingPeriod, setHomePageListing, setListing, setListings, setPublicListings, setReviewResponse, setReviewSummary } from ".";
import { api_url, baseUrl } from "../api";
import { Dispatch } from "redux";
import { z } from "zod";

import { base64ToFile, objectToFormData } from "@/src/lib/functions";
import { AppImage } from "@/src/components/page/dashboard/landlords/listings/upload/add-images";
import { addDiscountData, AddDiscountPayload, EditDiscountPayload, EnableDiscountPayload, FilterState, listingDiscountData, ReviewResponse, unavailabilityDto, UpdateListingImagesPayload } from "./interface";
import { SearchFilters } from "@/src/components/page/home/hero/search";
import { DiscountPeriod } from "@/src/components/page/dashboard/landlords/listings/discount-modal";




export enum PRICE_INTERVAL {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}
export enum SIZE_UNIT {
  SQ_FT = 'sq_ft',
  SQ_M = 'sq_m',
}

export enum CURRENCY {
  USD = 'USD',
  NGN = 'NGN',
}

export enum LISTING_TYPE {
  EVENT_CENTER = 'event_center',
  RETAIL = 'retail',
  EXPERIENCES = 'experiences',
  BEACHFRONT_VILLAS = 'beachfront_villas',
  APARTMENTS = 'apartments',
  SIGHTSEEING_TOURS = 'sightseeing_tours',
  PRIVATE_BOAT_CRUISE = 'private_boat_cruise',
  // PREMIUM = 'premium',
  // ICONIC = 'iconic',
}


export enum LISTING_VERIFICATION_STATUS {
  NONE = 'none',
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum LISTING_QUALITY {
  STANDARD = 'standard',
  PREMIUM = 'premium',
  ICONIC = 'iconic',
}

export enum LISTING_RATINGS {
  EXCELLENT = 'excellent',
  GOOD = 'good',
  AVERAGE = 'average',
  BAD = 'bad',
}

export const LISTING_RATINGS_MAP = {
  [LISTING_RATINGS.EXCELLENT]: 5,
  [LISTING_RATINGS.GOOD]: 4,
  [LISTING_RATINGS.AVERAGE]: 3,
  [LISTING_RATINGS.BAD]: 2,
};

export enum PUBLIC_LISTING_CATEGORIES {
  LATEST = 'latest',
  POPULAR = 'popular',
  NEAREST = 'nearest',
  RETAIL = 'retail',
  RESIDENCE = 'residence',
  EXPERIENCES = 'experiences',
  OTHER_EXPERIENCES = 'other_experiences',
  EVENT_CENTER = 'event_center',
  BEACHFRONT_VILLAS = 'beachfront_villas',
  APARTMENTS = 'apartments',
  SIGHTSEEING_TOURS = 'sightseeing_tours',
  PRIVATE_BOAT_CRUISE = 'private_boat_cruise',
  PREMIUM = 'premium',
  ICONIC = 'iconic',
  ALL = 'all',
}



interface Range {
  from?: string;
  to?: string;
}

// Interface equivalent to ListingFilters
interface ListingFilters {
  location?: string;
  rating?: LISTING_RATINGS;
  use_cases?: string[];
  amenities?: string[];
  price?: Range;
  size?: Range;
  verification_status?: LISTING_VERIFICATION_STATUS;
}

// Interface equivalent to ListingPagination
interface ListingPagination {
  per_page?: number;
  page?: number;
}

// Interface equivalent to PublicListingQueryDto
export interface ListingParams {
  location?: string;
  filters?: FilterState;
  pagination?: ListingPagination;
  is_paginated?: boolean;
  search?: string;
  sort?:Record<string, 1 | -1>;
  category?: PUBLIC_LISTING_CATEGORIES;
}

export enum BOOKING_STATUS {
  PENDING = 'pending',
  PAID = 'paid',
  APPROVED = 'approved',
  IN_USE = 'in_use',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}
export interface TimeRange {
  start_time?: string;
  end_time?: string;
}

export interface Availability {
  alwaysAvailable: boolean;
  timeRange?: TimeRange;
}

export interface createLisiting {
  description: string;
  additional_information:string,
  name: string;
  input_address: string;
  formatted_address: string;
  tags?: string[];
  type:string;
  use_cases?: string[];
  base_price: number;
  base_price_interval: string;
  size: number;
  extras?: {
    amenities?: string[];
    accessibility?: string[];
    features?: {
      name: string;
      description: string;
      price: number;
      interval: string;
    }[];
  };
  images: AppImage[];
  variable_pricing?: Record<string, string>;
  availability?: Availability; // Add availability field
  disclaimer?:string

}

interface updateLisiting extends createLisiting {
  id: string;
  current_images?: string[];
  place_images_index?:number|any
 
}
interface DeleteUnavailablePeriodParams {
  listingId: string;
  periodId: string;
}


export const getPublicListingsRequest = 
  (params?: ListingParams) => async (dispatch: Dispatch) => {
    try {
      dispatch({ type: "LISTINGS_LOADING", payload: true });
      const response = await axios.get(`${baseUrl.listings}/public`, { params });
      return dispatch(setPublicListings(response.data));
    } catch (error: unknown) {
      const axiosError = error as AxiosError|any;
      console.error("Error fetching listings:", axiosError.message);
      emit(ALERT_EVENTS.ERROR, axiosError.response?.data?.message || "An error occurred");
      dispatch({ type: "LISTINGS_ERROR", payload: axiosError.message });
    } finally {
      dispatch({ type: "LISTINGS_LOADING", payload: false });
    }
};


    
export const getListingsRequest=()=>async(dispatch:Dispatch)=>{
        try {
            const response = await axios.get(`${baseUrl.listings}`)
            dispatch(setListings(response.data))
        } catch (error) {
            emit(ALERT_EVENTS.ERROR, error)
        }
}

export const createListingRequest = (data: createLisiting) => async (dispatch: Dispatch) => {
  try {
    const { images, ...rest } = data;

    // Convert images to files (handling your existing logic)
    const imgs = images
      .map((img: AppImage) => (img.src ? base64ToFile(img.src, img.name || "image.jpg") : null))
      .filter((file): file is File => file !== null);

    // Create FormData object
    const formData = new FormData();

    // Normalize use_cases to an array
    const normalizedUseCases = Array.isArray(rest.use_cases)
      ? rest.use_cases
      : rest.use_cases
        ? [rest.use_cases]
        : []; // Default to empty array if undefined

    const normalizedData = {
      ...rest,
      use_cases: normalizedUseCases,
    };

    // Append data to FormData
    for (const key in normalizedData) {
      if (Object.prototype.hasOwnProperty.call(normalizedData, key)) {
        let value = normalizedData[key as keyof typeof normalizedData];

        if (key === 'use_cases' && Array.isArray(value)) {
          // Append each use case with an indexed key
          value.forEach((useCase: string, index: number) => {
            formData.append(`use_cases[${index}]`, useCase);
          });
        } else if (['extras', 'variable_pricing', 'availability'].includes(key)) {
          // Append as JSON strings for complex objects
          formData.append(key, JSON.stringify(value));
        } else {
          // Append primitive values directly
          formData.append(key, value as string);
        }
      }
    }

    // Append images
    imgs.forEach((file: File, index: number) => {
      formData.append(`images[${index}]`, file);
    });

    // Send the request
    const response = await axios.post(`${baseUrl.listings}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return [response.data, null]; // Success
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || "An error occurred";
    return [null, errorMessage]; // Error
  }
};

export const updateListingRequest = (data: updateLisiting) => async (dispatch: Dispatch) => {
  try {
    const { images, current_images, place_images_index, ...rest } = data;

    // Convert images to files (handling your existing logic)
    const imgs = images
      .map((img: AppImage) => (img.src ? base64ToFile(img.src, img.name || "image.jpg") : null))
      .filter((file): file is File => file !== null);

    // Create FormData object
    const formData = new FormData();

    // Normalize use_cases to an array
    const normalizedUseCases = Array.isArray(rest.use_cases)
      ? rest.use_cases
      : rest.use_cases
        ? [rest.use_cases]
        : []; // Default to empty array if undefined

    const normalizedData = {
      ...rest,
      use_cases: normalizedUseCases,
    };

    // Append data to FormData
    for (const key in normalizedData) {
      if (Object.prototype.hasOwnProperty.call(normalizedData, key)) {
        let value = normalizedData[key as keyof typeof normalizedData];

        if (key === 'use_cases' && Array.isArray(value)) {
          // Append each use case with an indexed key
          value.forEach((useCase: string, index: number) => {
            formData.append(`use_cases[${index}]`, useCase);
          });
        } else if (['extras', 'variable_pricing', 'availability'].includes(key) && value !== undefined) {
          // Append as JSON strings for complex objects
          formData.append(key, JSON.stringify(value));
        } else if (value !== undefined) {
          // Append primitive values directly as strings
          formData.append(key, value as string);
        }
      }
    }

    // Append current_images if provided
    if (current_images && current_images.length > 0) {
      current_images.forEach((image: string, index: number) => {
        formData.append(`current_images[${index}]`, image);
      });
    }

    // Append place_images_index if provided
    if (place_images_index !== undefined) {
      formData.append('place_images_index', place_images_index);
    }

    // Append images
    imgs.forEach((file: File, index: number) => {
      formData.append(`images[${index}]`, file);
    });

    // Send the request
    const response = await axios.put(`${baseUrl.listings}/${data.id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return [response.data, null]; // Success
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || "An error occurred";
    emit(ALERT_EVENTS.ERROR, errorMessage);
    return [null, errorMessage]; // Error
  }
};


export const updateListingImagesRequest = (data: UpdateListingImagesPayload) => async (dispatch: Dispatch | any) => {
  try {
    const { id, images: appImages, currentImages, insertIndex } = data;

    // Convert AppImage objects (with base64 src) to File objects
    const filesToUpload: File[] = (appImages || [])
      .map((img: AppImage) => {
        if (img.src && typeof img.src === 'string' && img.src.startsWith('data:')) {
          const fileName = img.name || `new_image_${Date.now()}.${img.src.substring(img.src.indexOf('/') + 1, img.src.indexOf(';')) || 'jpg'}`;
          return base64ToFile(img.src, fileName);
        }
        return null;
      })
      .filter((file): file is File => file !== null);

    const formData = new FormData();

    // Append new image files
    // Backend expects files under the key 'images' (matching UpdateListingImagesDto.images)
    filesToUpload.forEach((file: File) => {
      formData.append('images', file, file.name);
    });

    // Append currentImages (URLs of images to keep)
    // Backend expects 'currentImages[]' (matching UpdateListingImagesDto.currentImages as an array)
    if (currentImages && currentImages.length > 0) {
      currentImages.forEach((imageUrl: string) => {
        formData.append('currentImages[]', imageUrl);
      });
    }

    // Append insertIndex if provided
    // Backend expects 'insertIndex' (matching UpdateListingImagesDto.insertIndex as a number)
    if (typeof insertIndex === 'number') {
      formData.append('insertIndex', String(insertIndex));
    }

    // Send the PATCH request
    const response = await axios.patch(`${baseUrl.listings}/${id}/images`, formData);

    // Dispatch success action (example)
    // if (dispatch) {
    //   dispatch({ type: 'UPDATE_LISTING_IMAGES_SUCCESS', payload: response.data.data });
    // }
    return [response.data.data, null];

  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while updating listing images.';
    // Dispatch error action (example)
    // if (dispatch) {
    //   dispatch({ type: 'UPDATE_LISTING_IMAGES_FAILURE', payload: errorMessage });
    // }
    emit(ALERT_EVENTS.ERROR, errorMessage);
    return [null, errorMessage];
  }
};

    

    export const getListingByIDRequest =(data:string)=>async(dispatch:Dispatch)=>{
      try {

        
        const response = await axios.get(`${baseUrl.listings}/${data}`);
        dispatch(setListing(response.data))

        
       
        return [response.data,null ]
        
      } catch (error:any) {
        const errorMessage = error.response?.data?.message || error.message || "An error occurred";
        emit(ALERT_EVENTS.ERROR, errorMessage);
    
        return [null, errorMessage]; // 
      }
    }
    export const getListingByIDRequestForAdmin =(data:string)=>async(dispatch:Dispatch)=>{
      try {

        const token = sessionStorage.getItem("token");

        const response = await axios.get(`${baseUrl.listings}/super-admin/${data}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        dispatch(setListing(response.data))

        return [response.data,null ]
        
      } catch (error:any) {
        const errorMessage = error.response?.data?.message || error.message || "An error occurred";
        emit(ALERT_EVENTS.ERROR, errorMessage);
    
        return [null, errorMessage]; // 
      }
    }

    export const requestReviewRequest = 
    (data: { id: string; docs: File[] }) => 
    async (dispatch: Dispatch) => {
      try {
        const { docs, id } = data;
  
        // Initialize FormData and append files with proper keys (files[0], files[1], ...)
        const formData = new FormData();
        docs.forEach((file: File, index: number) => {
          formData.append(`files[${index}]`, file);
        });
  
        // Perform the API request
        const response = await axios.put(
          `${baseUrl.listings}/review/${id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
  
        // Return the response data and null error
        return [response.data, null];
      } catch (error: any) {
        // Extract error message
        const errorMessage = error.response?.data?.message || error.message || "An error occurred";
  
        // Emit an error event
        emit(ALERT_EVENTS.ERROR, errorMessage);
  
        // Return null data and the error message
        return [null, errorMessage];
      }
    };

    // export const getListingByIDRequest

  

    export const removeListingsRequest =(data:string)=>async( dispatch:Dispatch)=>{
      try{
        const response = await axios.delete(`${baseUrl.listings}/${data}`);
        return true
      }catch(error:any){
        return false
      }
    }

    export const addListingUnavailabilityPeriod =  (data: unavailabilityDto, id: string) => 
    async (dispatch: Dispatch): Promise<any> => {
      try {
        // Make the API request to add unavailability period
        const response = await axios.post(`${baseUrl.listings}/${id}/unavailability`, data);
  
        // Return the response data
        return true
      } catch (error: any) {
        // Extract error message
        const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
  
        // Emit an error event for global error handling
        emit(ALERT_EVENTS.ERROR, errorMessage);
  
        // Return null to indicate failure
        return null;
      }


    };


    
/**
|--------------------------------------------------
| Delete an unavailable period by sending dates in the request body
|--------------------------------------------------
*/
export const deleteUnavailablePeriodRequest =
  ({ listingId, start_date, end_date }: { listingId: string; start_date: Date; end_date: Date }) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await axios.delete(`${baseUrl.listings}/${listingId}/unavailability`, {
        data: { start_date, end_date }, // send body with DELETE request
      });

      return true
    } catch (error: any) {
      return error.response?.data?.message || 'Failed to delete unavailable period';
    }
  };

    export const getBookedDatesRequest = (id: string) => async (dispatch: Dispatch): Promise<any> => {
      try {
        // Make the API request to get booked dates
        const response = await axios.get(`${baseUrl.booking}/${id}/booked_dates`);

        dispatch(setBookingPeriod(response.data));
      } catch (error: any) {
        // Extract error message
        const errorMessage = error.response?.data?.message || error.message || 'An error occurred';

        // Emit an error event for global error handling
        emit(ALERT_EVENTS.ERROR, errorMessage);

        // Return null to indicate failure
        return null;
      }
    }

    export const FilterHomePageListingRequest=(data:SearchFilters)=> async(dispatch:Dispatch)=>{
      try {
        const response = await axios.post(`${baseUrl.listings}/home-page`, data)
        dispatch(setHomePageListing(response.data))
      } catch (error) {
        console.log(error);
        
      }
    }

export const addListingDiscountRequest = (id: string, data: AddDiscountPayload) => async (dispatch: Dispatch) => {
  try {
    const response = await axios.post(`${baseUrl.listings}/${id}/discounts`, data);

    return true; // Return the new discount data
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while adding the discount';
    emit(ALERT_EVENTS.ERROR, errorMessage);
    return null;
  }
};


// --- EDIT DISCOUNT ---
// This aligns with the backend's editDiscount method (PUT /listings/:listingId/discounts?startDate=...&endDate=...)
export const editListingDiscountRequest = (
  id: string,
  originalStartDate: Date, // The original start date to identify the discount
  originalEndDate: Date,   // The original end date to identify the discount
  updates: EditDiscountPayload // The partial data to update
) => async (dispatch: Dispatch) => {
  try {
    // Ensure dates are correctly formatted for URL parameters (ISO string)
    // The backend expects Date objects, so ISO strings are a good choice for transfer.
    const startDateParam = new Date(originalStartDate).toISOString();
    const endDateParam = new Date(originalEndDate).toISOString();

    const response = await axios.put(
      `${baseUrl.listings}/${id}/discounts?startDate=${startDateParam}&endDate=${endDateParam}`,
      updates // The request body contains the fields to update
    );

    // Optionally dispatch a success action
    // dispatch({ type: 'EDIT_LISTING_DISCOUNT_SUCCESS', payload: response.data });
    console.log('Discount edited successfully:', response.data);

    return response.data; // Return the updated discount data
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while editing the discount';
    emit(ALERT_EVENTS.ERROR, errorMessage);
    return null;
  }
};


// --- ENABLE/DISABLE DISCOUNT ---
// This aligns with the backend's enableDiscount method (PATCH /listings/:listingId/discounts/enable?startDate=...&endDate=...)
export const toggleDiscountRequest = (
  id: string,
  targetStartDate: Date, // The start date of the discount to enable/disable
  targetEndDate: Date,   // The end date of the discount to enable/disable
  payload: EnableDiscountPayload // { enable_discount: boolean }
) => async (dispatch: Dispatch) => {
  try {
    const startDateParam = new Date(targetStartDate).toISOString();
    const endDateParam = new Date(targetEndDate).toISOString();

    const response = await axios.patch(
      `${baseUrl.listings}/${id}/discounts/enable?startDate=${startDateParam}&endDate=${endDateParam}`,
      payload // The request body contains { enable_discount: boolean }
    );

    if(response.status === 200){
      return true
    }
    return false
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while changing discount status';
    emit(ALERT_EVENTS.ERROR, errorMessage);
    return false
  }
};



export const deleteListingDiscountRequest = (
  id: string,
  startDateToDelete: Date,
  endDateToDelete: Date     
) => async (dispatch: Dispatch) => {
  try {
    const startDateParam = new Date(startDateToDelete).toISOString();
    const endDateParam = new Date(endDateToDelete).toISOString();

    const response = await axios.delete(
      `${baseUrl.listings}/${id}/discounts?startDate=${startDateParam}&endDate=${endDateParam}`
    );


    console.log('Discount deleted successfully:', response.data);

    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred while deleting the discount';
    emit(ALERT_EVENTS.ERROR, errorMessage);
    return null;
  }
};

interface Review {
  id: string;
  starRating: number;
  reviewComment: string;
  // Add other review fields as needed
}

interface Pagination {
  currentPage: number;
  reviewsPerPage: number;
  totalReviews: number;
  totalPages: number;
}



export const getReviewsByListing =
  (id: string, page: number = 1, limit: number = 5) =>
  async (dispatch: Dispatch): Promise<ReviewResponse | null> => {
    try {
      // Validate page parameter
      if (page < 1) {
        throw new Error('Page number must be at least 1');
      }

      // API request with query parameters
      const response = await axios.get<ReviewResponse>(
        `${baseUrl.reviews}/${id}?page=${page}&limit=${limit}`,
      );

      // Dispatch the response data
      dispatch(setReviewResponse(response.data));

      return response.data;
    } catch (error: any) {
      // Map backend errors to user-friendly messages
      let errorMessage: string;
      if (error.response?.status === 404) {
        errorMessage = error.response?.data?.message || 'Listing or reviews not found';
      } else {
        errorMessage = error.response?.data?.message || error.message || 'Failed to fetch reviews';
      }

      // Emit error alert
      emit(ALERT_EVENTS.ERROR, errorMessage);

      return null;
    }
  };

export const getReviewSummaryByListingRequest =(id:string)=>async(dispatch:Dispatch)=>{
  console.log(id);
  
  try {
    const response = await axios.get(`${baseUrl.reviews}/review-summary/${id}`)
    dispatch(setReviewSummary(response.data))
    return response.data
} catch (error: any) {
      // Map backend errors to user-friendly messages
      let errorMessage: string;
      if (error.response?.status === 404) {
        errorMessage = error.response?.data?.message || 'Listing or reviews not found';
      } else {
        errorMessage = error.response?.data?.message || error.message || 'Failed to fetch reviews';
      }

      // Emit error alert
      emit(ALERT_EVENTS.ERROR, errorMessage);

      return null;
    }
}
export const UpdateListingDisclaimer = async (listingId: string, data: { disclaimer: string }) => {
  try {
    const response = await axios.patch(`${baseUrl.listings}/${listingId}/disclaimer`, data)
    return [response.data, null]
  } catch (error: any) {
    return [null, error.response?.data || error]
  }
}